import { type ApolloError, useQuery, useSubscription } from '@apollo/client'
import { useState } from 'react'

import { isNotNull } from '@/utils/helpers'
import logger from '@/utils/logger'

import { useUser } from '@/client/context/user'
import { useIsPageVisible } from '@/client/hooks'
import {
  POST_EVENTS_SUBSCRIPTION,
  POST_FEEDBACKS_SUBSCRIPTION,
  POST_SINGLE_QUERY,
  POST_UPDATES_SUBSCRIPTION,
} from '@/client/services/hasura'
import type {
  PostEventsSubscription,
  PostEventsSubscriptionVariables,
  PostFeedbackSubscription,
  PostFeedbackSubscriptionVariables,
  PostQuery,
  PostQueryVariables,
  PostUpdatesSubscription,
  PostUpdatesSubscriptionVariables,
} from '@/client/services/hasura/generated/graphql'

import type {
  PostDetails,
  PostEventDetails,
  PostFeedbackDetails,
} from '@/components/Community/Post/types'
import type { UserDetails } from '@/components/Worlds/types'

import {
  partialPostDetailsFromPostUpdates,
  postDetailsFromFragment,
  postEventDetailsFromFragment,
  postFeedbackDetailsFromFragment,
  userDetailsFromFragment,
} from '../../transforms/detailsFromFragments'

export default function useRealtimePost(postId: number): {
  loading: boolean
  error: ApolloError | null
  post: PostDetails | null
  currentUser: UserDetails | null
  postEvents: PostEventDetails[]
  postFeedbacks: PostFeedbackDetails[] | null
} {
  const user = useUser()
  const isPageVisible = useIsPageVisible()

  const [post, setPost] = useState<PostDetails | null>(null)
  const [postEvents, setPostEvents] = useState<PostEventDetails[]>([])
  const [postFeedbacks, setPostFeedbacks] = useState<PostFeedbackDetails[] | null>(null)
  const [currentUser, setCurrentUser] = useState<UserDetails | null>(null)

  const { loading, error = null } = useQuery<PostQuery, PostQueryVariables>(POST_SINGLE_QUERY, {
    variables: {
      id: postId,
      currentUserId: user.id,
    },
    onCompleted: data => {
      const postFragment = data.post_by_pk
      const userFragment = data.user_by_pk
      const postDetails = postFragment != null ? postDetailsFromFragment(postFragment) : null
      const userDetails = userFragment != null ? userDetailsFromFragment(userFragment) : null

      if (postDetails != null) {
        setPost(postDetails)
        setPostEvents(
          postFragment?.post_events
            ?.map(postEvent => postEventDetailsFromFragment({ postEvent }))
            .filter(isNotNull) ?? []
        )
        setCurrentUser(userDetails)
      }
    },
    onError: err => {
      logger.warning('Error querying Hasura for Post', { err })
    },
  })

  useSubscription<PostUpdatesSubscription, PostUpdatesSubscriptionVariables>(
    POST_UPDATES_SUBSCRIPTION,
    {
      skip: !isPageVisible || loading || error != null,
      variables: {
        id: postId,
      },
      onData: data => {
        const postUpdates = data.data?.data?.post_by_pk
        const partialPostDetails = partialPostDetailsFromPostUpdates(postUpdates)

        setPost(prevPost =>
          prevPost == null
            ? null
            : {
                ...prevPost,
                ...partialPostDetails,
                liveHelpInfo:
                  prevPost.liveHelpInfo != null
                    ? { ...prevPost.liveHelpInfo, ...partialPostDetails.liveHelpInfo }
                    : null,
              }
        )
      },
    }
  )

  useSubscription<PostEventsSubscription, PostEventsSubscriptionVariables>(
    POST_EVENTS_SUBSCRIPTION,
    {
      skip: !isPageVisible || loading || error != null,
      variables: {
        id: postId,
      },
      onData: data => {
        const newPostEvents = data.data?.data?.post_by_pk?.post_events ?? []

        setPostEvents(
          newPostEvents
            .map(postEvent => postEventDetailsFromFragment({ postEvent }))
            .filter(isNotNull) ?? []
        )
      },
    }
  )

  useSubscription<PostFeedbackSubscription, PostFeedbackSubscriptionVariables>(
    POST_FEEDBACKS_SUBSCRIPTION,
    {
      skip: !isPageVisible || loading || error != null,
      variables: {
        id: postId,
      },
      onData: data => {
        const newPostFeedbacks = data.data?.data?.post_by_pk?.post_feedbacks ?? null

        setPostFeedbacks(
          newPostFeedbacks == null
            ? null
            : newPostFeedbacks.map(postFeedback => postFeedbackDetailsFromFragment(postFeedback))
        )
      },
    }
  )

  return {
    loading: loading && (post == null || postFeedbacks == null),
    error,
    post,
    currentUser,
    postEvents,
    postFeedbacks,
  }
}
